// src/shared/components/ui/ConfirmDialog.jsx
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Box,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';

/**
 * Reusable confirmation dialog.
 *
 * Variants:
 *  - "default" — neutral confirmation (e.g. "Mark as inactive?")
 *  - "destructive" — irreversible action (delete, cancel, remove). Shows a
 *    warning icon and a red confirm button to make the consequences obvious.
 *
 * Props:
 *  - open, onClose, onConfirm — required
 *  - title, message — text content
 *  - confirmText, cancelText — button labels
 *  - variant — "default" | "destructive"
 *  - busy — when true, disables both buttons + shows a working confirm label
 */
const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  message = '',
  confirmText,
  cancelText = 'Cancel',
  variant = 'default',
  busy = false,
}) => {
  const isDestructive = variant === 'destructive';
  const Icon = isDestructive ? WarningAmberRoundedIcon : HelpOutlineRoundedIcon;
  const iconColor = isDestructive ? 'error' : 'primary';
  const finalConfirmText = confirmText ?? (isDestructive ? 'Delete' : 'Confirm');

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1.5} sx={{
          alignItems: "center"
        }}>
          <Box
            sx={(theme) => ({
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              background: alpha(
                isDestructive ? theme.palette.error.main : theme.palette.primary.main,
                0.12,
              ),
            })}
          >
            <Icon color={iconColor} fontSize="small" />
          </Box>
          <span>{title}</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {typeof message === 'string' ? (
          <Typography sx={{
            color: "text.secondary"
          }}>{message}</Typography>
        ) : (
          message
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={isDestructive ? 'error' : 'primary'}
          disabled={busy}
        >
          {busy ? 'Working…' : finalConfirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
